import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Sparkles, Loader2, Check, RefreshCw } from 'lucide-react';

const API = window.location.origin;
const QUICK_ACTIONS = ['Improve the wording', 'Make it friendlier', 'Make it more professional', 'Make it shorter', 'Fix spelling & grammar'];

// Reusable "AI help" button for any text field. Opens a dialog where the user
// either describes what they want to say (empty field) or asks the AI to
// rewrite/improve the current text. Apply passes the suggestion back via onApply.
export const AIWordingButton = ({ value, onApply, context, isHtml = false, testId = 'ai-wording-btn' }) => {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(false);

  const hasText = Boolean((value || '').trim());

  const generate = async (quickInstruction) => {
    const finalInstruction = (quickInstruction || instruction || '').trim();
    if (!finalInstruction) {
      toast.error(hasText ? 'Tell the AI how to change it, or pick a quick action' : 'Describe what you want to say');
      return;
    }
    if (quickInstruction) setInstruction(quickInstruction);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/ai/wording-assist`, {
        instruction: finalInstruction,
        current_text: value || '',
        context: context || '',
        is_html: isHtml,
      });
      setSuggestion(res.data?.suggestion || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI request failed — please try again');
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    onApply(suggestion);
    setOpen(false);
    setSuggestion('');
    setInstruction('');
    toast.success('Applied — remember to save');
  };

  const close = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) { setSuggestion(''); setInstruction(''); }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs text-primary hover:text-primary gap-1"
        onClick={() => setOpen(true)}
        data-testid={testId}
        title="AI writing help"
      >
        <Sparkles className="h-3.5 w-3.5" />
        AI
      </Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI writing help
            </DialogTitle>
            {context && <DialogDescription>{context}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-3">
            {hasText && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Current text</p>
                <div className="text-sm bg-muted rounded-md p-2 max-h-28 overflow-y-auto whitespace-pre-wrap">{value}</div>
              </div>
            )}
            {hasText && (
              <div className="flex flex-wrap gap-1.5">
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action}
                    type="button"
                    onClick={() => generate(action)}
                    disabled={loading}
                    className="text-xs px-2.5 py-1 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                    data-testid={`${testId}-quick-${action.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  >
                    {action}
                  </button>
                ))}
              </div>
            )}
            <div>
              <Textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                placeholder={hasText
                  ? 'Or tell the AI what to change, e.g. "mention we\'re fully booked until August"'
                  : 'Describe what you want to say, e.g. "busy right now, reply in 2 weeks, WhatsApp if urgent"'}
                rows={2}
                data-testid={`${testId}-instruction`}
              />
            </div>
            <Button onClick={() => generate()} disabled={loading} className="w-full" data-testid={`${testId}-generate`}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {loading ? 'Writing…' : suggestion ? 'Regenerate' : 'Generate'}
            </Button>
            {suggestion && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Suggestion</p>
                <div className="text-sm bg-green-50 border border-green-200 rounded-md p-3 max-h-48 overflow-y-auto whitespace-pre-wrap" data-testid={`${testId}-suggestion`}>
                  {suggestion}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
            {suggestion && (
              <>
                <Button variant="outline" onClick={() => generate()} disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Try again
                </Button>
                <Button onClick={apply} data-testid={`${testId}-apply`}>
                  <Check className="h-4 w-4 mr-1" /> Use this
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AIWordingButton;
